import { useState, useEffect } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import { createFtpUser, getPlainCode } from '../api/ftpUser.api'; 
import { Copy, Eye, EyeOff } from 'lucide-react'; 

export interface FtpUser {
  id?: number;
  customerId: string;
  username: string;
  password: string;
  isMulti: boolean;
  storageLimitGb?: number;
  pathDefault: string;
  status: 'active' | 'suspended';
  camIdentifier: string;
}

interface FtpUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCloseCancel: () => void;
  ftpUser?: FtpUser;
  customerId?: string;
  camIdentifier?: string;
}

const FtpUserComponent: React.FC<FtpUserModalProps> = ({ isOpen, onClose, onCloseCancel, ftpUser, customerId, camIdentifier }) => {
  const [loading, setLoading] = useState(false);
  const [showPlainPassword, setShowPlainPassword] = useState(false);
  const [plainPassword, setPlainPassword] = useState<string | null>(null);

  const [formData, setFormData] = useState<FtpUser>({
    customerId: customerId || "",
    username: "",
    password: "",
    isMulti: false,
    pathDefault: "",
    storageLimitGb: 50,
    status: "active",
    camIdentifier: camIdentifier || "",
  });

  useEffect(() => {
    if (ftpUser) {
      setFormData(ftpUser);
    } else {
      setFormData({
        customerId: customerId || "",
        username: "",
        password: "",
        isMulti: false,
        pathDefault: "",
        storageLimitGb: 50,
        status: "active",
        camIdentifier: camIdentifier || "",
      });
    }
    setPlainPassword(null);
    setShowPlainPassword(false);
  }, [ftpUser, customerId, camIdentifier, isOpen]);

  const generateRandomString = (length: number, prefix: string = ""): string => {
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789';
    let result = prefix;
    const charactersLength = characters.length;
    for (let i = 0; i < length - prefix.length; i++) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  };

  const saveUserData = async (dataToSave: FtpUser) => {
    if (dataToSave.id) return; // No permitir update aquí

    setLoading(true);
    try {
      const payload = { 
        ...dataToSave,
        pathDefault: dataToSave.pathDefault?.trim() || "TIME"
      };

      const response = await createFtpUser(payload);
      toast.success("Usuario FTP creado y guardado exitosamente.");
      setFormData(prev => ({ ...prev, ...response }));
    } catch (error) {
      console.error("Error saving FTP user:", error);
      toast.error("Error al conectar con el servidor. Verifique si el usuario ya existe.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAndSave = async () => {
    const generatedUsername = generateRandomString(12, "li");
    const characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let generatedPassword = "";
    for (let i = 0; i < 12; i++) {
      generatedPassword += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    const newUserData = {
      ...formData,
      username: generatedUsername,
      password: generatedPassword,
    };

    setFormData(newUserData);
    await saveUserData(newUserData);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    let inputValue: any;
    if (type === "checkbox") inputValue = (e.target as HTMLInputElement).checked;
    else if (type === "number") inputValue = value === "" ? undefined : Number(value);
    else inputValue = value;
    setFormData(prev => ({ ...prev, [name]: inputValue }));
  };

  const copyToClipboard = (text: string, fieldName: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text)
      .then(() => toast.success(`${fieldName} copiado!`))
      .catch(() => toast.error("Error al copiar"));
  };

  const handleShowPlainPassword = async () => {
    if (showPlainPassword) {
      setShowPlainPassword(false);
      return;
    }

    if (plainPassword) {
      setShowPlainPassword(true);
      return;
    }

    try {
      const response = await getPlainCode(formData.customerId, formData.camIdentifier);
      setPlainPassword(response.plainCode || response.password || response); // Dependiendo de cómo responda el endpoint
      setShowPlainPassword(true);
    } catch (error) {
      console.error("Error fetching plain password:", error);
      toast.error("Error al obtener la contraseña en texto plano");
    }
  };

  if (!isOpen) return null;

  const isCreated = !!formData.id;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-start pt-12 z-[60] overflow-y-auto">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl my-8 mx-4">
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-xl font-bold">{isCreated ? 'Detalles Usuario FTP' : 'Nuevo Usuario FTP'}</h2>
          <button className="text-gray-500 hover:text-gray-700 text-2xl" onClick={onCloseCancel}>&times;</button>
        </div>

        <div>
          <form onSubmit={(e) => { e.preventDefault(); if (!isCreated) saveUserData(formData); }}>
            <div className="mb-4">
              <label className="block text-gray-700 font-medium mb-1">Cámara Vinculada</label>
              <input type="text" value={formData.camIdentifier} className="w-full p-2 border bg-gray-50 rounded-md outline-none text-gray-500" readOnly />
            </div>

            <div className="mb-4">
              <label htmlFor="pathDefault" className="block text-gray-700 font-medium mb-1">Ruta Personalizada (Opcional)</label>
              <input 
                type="text" 
                id="pathDefault" 
                name="pathDefault" 
                value={formData.pathDefault} 
                onChange={handleInputChange} 
                className={`w-full p-2 border rounded-md outline-none ${isCreated ? 'bg-gray-50 text-gray-500' : 'focus:ring-2 focus:ring-blue-500'}`}
                placeholder="TIME (envía por defecto)"
                disabled={isCreated}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="flex items-center pt-6">
                <input 
                  type="checkbox" 
                  id="isMulti" 
                  name="isMulti" 
                  checked={formData.isMulti} 
                  onChange={handleInputChange} 
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded" 
                  disabled={isCreated}
                />
                <label htmlFor="isMulti" className="ml-2 block text-gray-700 text-sm font-medium">Múltiples rutas (config. en cámara)</label>
              </div>
              <div>
                <label className="block text-gray-700 text-sm font-medium mb-1">Límite Storage (GB)</label>
                <input 
                  type="number" 
                  name="storageLimitGb" 
                  value={formData.storageLimitGb || ''} 
                  onChange={handleInputChange} 
                  className={`w-full p-2 border rounded-md text-sm ${isCreated ? 'bg-gray-50 text-gray-500' : ''}`}
                  placeholder="50" 
                  disabled={isCreated}
                />
              </div>
            </div>

            <div className="mb-6">
              <label className="block text-gray-700 font-medium mb-1 after:content-['*'] after:ml-1 after:text-red-500">Estado</label>
              <select 
                name="status" 
                value={formData.status} 
                onChange={handleInputChange} 
                className={`w-full p-2 border rounded-md ${isCreated ? 'bg-gray-50 text-gray-500' : ''}`}
                disabled={isCreated}
              >
                <option value="active">ACTIVE</option>
                <option value="suspended">SUSPENDED</option>
              </select>
            </div>

            {formData.username && (
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 mb-6 animate-fade-in">
                <p className="text-xs font-bold text-gray-400 uppercase mb-3 text-center">Credenciales de Acceso Generadas</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Usuario (FeedID)</label>
                    <div className="flex shadow-sm">
                      <input type="text" value={formData.username} className="flex-1 p-2 border bg-white rounded-l-md text-sm font-mono outline-none" readOnly />
                      <button type="button" onClick={() => copyToClipboard(formData.username, 'Usuario')} className="px-3 border border-l-0 bg-white hover:bg-gray-100 rounded-r-md">
                        <Copy className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Contraseña</label>
                    <div className="flex shadow-sm">
                      <input 
                        type={showPlainPassword ? "text" : "password"} 
                        value={showPlainPassword ? (plainPassword || formData.password) : formData.password} 
                        className="flex-1 p-2 border bg-white rounded-l-md text-sm font-mono outline-none" 
                        readOnly 
                      />
                      <button 
                        type="button" 
                        onClick={handleShowPlainPassword} 
                        className="px-3 border border-l-0 bg-white hover:bg-gray-100"
                        title="Ver contraseña"
                      >
                        {showPlainPassword ? <EyeOff className="w-4 h-4 text-gray-500" /> : <Eye className="w-4 h-4 text-gray-500" />}
                      </button>
                      <button 
                        type="button" 
                        onClick={() => copyToClipboard(showPlainPassword && plainPassword ? plainPassword : formData.password, 'Contraseña')} 
                        className="px-3 border border-l-0 bg-white hover:bg-gray-100 rounded-r-md"
                      >
                        <Copy className="w-4 h-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-3 border-t pt-6">
              {!isCreated && (
                <button
                  type="button"
                  onClick={handleGenerateAndSave}
                  disabled={loading}
                  className="w-full py-3 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-bold shadow-md disabled:bg-green-300"
                >
                  {loading ? "Creando usuario..." : "Generar y Crear Usuario FTP"}
                </button>
              )}
              
              <button 
                type="button" 
                className={`w-full py-2 font-medium transition-colors ${isCreated ? 'bg-blue-600 text-white rounded-md hover:bg-blue-700' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={onClose}
              >
                Cerrar y volver
              </button>
            </div>
          </form>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default FtpUserComponent;
